<template lang="pug">
y-dialog(v-if="showDialog" v-model="showDialog" max-width=568 content-class="round-2xl")
  v-card
    div.d-flex.align-center.justify-center(v-if="isFetching" style="height: 20vh;")
      v-progress-circular(:width="2" :size="50" color="primary" indeterminate)
    template(v-else)
      v-card-title.pa-6
        template(v-if="approvalAction")
          .text-xl.font-medium.neutral700--text(v-if="approval") Appointment approved
          .text-xl.font-medium.neutral700--text(v-else) Appointment rejected
        template(v-else)
          .text-xl.font-medium.neutral700--text(v-if="approval") Approve this appointment?
          .text-xl.font-medium.neutral700--text(v-else) Are you sure you want to reject this appointment?
      v-card-text.px-6.py-0
        template(v-if="approvalAction")
          .text-md.neutral700--text(v-if="approval") The appointment request has been approved! Here's what you can do next:
          .text-md.neutral700--text(v-else) The appointment request has been rejected! Here's what you can do next:

          div.pa-4.mt-4.custom-box
            v-row(align="center" no-gutters)
              v-col
                .text-sm.font-semibold.neutral700--text WhatsApp patient
                .text-xs.neutral700--text.mt-1 Send appointment confirmation to patient via WhatsApp
              v-col(align="right" cols="4")
                y-button(label="WhatsApp" color="neutral700" size="sm" outlined prepend-icon="mdi-whatsapp" icon-color="neutral700" button-class="px-3" @click="clickWhatsapp")

          div.pa-4.mt-4.custom-box
            v-row(align="center" no-gutters)
              v-col
                .text-sm.font-semibold.neutral700--text Email patient
                .text-xs.neutral700--text.mt-1 Send a confirmation email to the patient. If an email was provided during booking, we've sent it automatically.
              v-col(v-if="showEmailField" cols="12")
                v-form(ref="form")
                  v-row.mt-4(no-gutters)
                    v-col
                      v-text-field.text-sm(v-model="email" outlined placeholder="Enter email" dense hide-details="auto" background-color="neutral100" :rules="Boolean(email) ? rules.valid_email : []" style="border-radius: 10px 0 0 10px;")
                    v-col(cols="2")
                      v-btn.text-sm.capitalize(color="neutral700" :loading="isLoading" depressed dark block style="border-radius: 0 10px 10px 0" @click="sendEmail") send
              v-col(v-else align="right" cols="4")
                y-button(label="Email sent" color="success500" size="sm" outlined prepend-icon="mdi-check" icon-color="success500" button-class="px-3")

          div.pa-4.mt-4.custom-box(v-if="approval")
            v-row(align="center" no-gutters)
              v-col
                .text-sm.font-semibold.neutral700--text View appointment details
                .text-xs.neutral700--text.mt-1 Access appointment specifics on the "Upcoming" tab in Appointments page.
              v-col(align="right" cols="4")
                y-button(label="Go to tab" color="neutral700" size="sm" outlined button-class="px-3" @click="goToTab")

        template(v-else)
          .text-md.neutral700--text This action will be done immediately and can’t be undone. Your patient will receive your rejection response through their email.
          .text-md.neutral700--text.mt-4(v-if="approval") Click “Approve” to approve the appointment
          .text-md.neutral700--text.mt-4(v-else) Click “Reject” to reject the appointment
      v-divider.mt-6
      v-card-actions.px-6.py-3
        template(v-if="approvalAction")
          v-spacer
          v-btn.button-md(:loading="isLoading" color="neutral700" depressed dark @click="closeResetDialog") Close
        template(v-else)
          v-btn.button-md.neutral700--text.px-0(text @click="closeDialog") {{ $t('cancel') }}
          v-spacer
          v-btn.button-md(v-if="approval" :loading="isLoading" color="neutral700" depressed dark @click="updateStatus") Approve
          v-btn.button-md(v-else :loading="isLoading" color="danger500" depressed dark @click="updateStatus") Reject
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  props: ['open', 'approval', 'selectedAppointment'],
  data() {
    return {
      isLoading: false,
      isFetching: false,
      approvalAction: false,
      emailSent: false,
      email: null,
      appointment: null,
      lead: null,
      rules: {
        valid_email: [ v => /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(v) || 'E-mail must be valid', ]
      }
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      isHq: 'hq/getIsHq',
      hqSelectedCompany: 'hq/getSelectedCompany'
    }),
    showDialog: {
      get () {
        return this.open
      },
      set (newValue) {
        this.$emit('update:open', newValue)
      }
    },
    showEmailField () {
      if (this.emailSent) {
        return false
      }
      if (this.appointment && this.appointment.lead && this.appointment.lead.email) {
        return false
      }
      return true
    },
    companySlug () {
      return this.isHq ? this.hqSelectedCompany.slug : this.company.slug
    }
  },
  mounted () {
    this.fetchAppointment()
    this.fetchLead()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchAppointment () {
      this.isFetching = true
      this.$axios.get(`/clinics/${this.companySlug}/appointments/${this.selectedAppointment.id}`)
        .then(resp => {
          this.appointment = resp.data
        })
        .catch(err => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response.data?.detail) {
            message = err.response.data.detail
          }
          this.showMessage(['error', message])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    fetchLead () {
      this.$axios.get(`/leads/${this.selectedAppointment.lead.pid}`)
        .then(resp => {
          this.lead = resp.data
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
        })
    },
    updateStatus () {
      let status = 'reject'
      if (this.approval) {
        status = 'new'
      }
      this.$axios.patch(`/clinics/${this.companySlug}/appointments/${this.appointment.id}`, { status })
        .then(resp => {
          this.$set(this.appointment, 'status', resp.data.status)
          this.approvalAction = true
        })
        .catch(err => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response.data?.detail) {
            message = err.response.data.detail
          }
          this.showMessage(['error', message])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    sendEmail () {
      if (!this.$refs.form.validate() || !this.email) {
        return
      }
      this.isLoading = true
      const payload = {
        email: this.email,
        type: 'appointment_request'
      }
      this.$axios.post(`/clinics/${this.companySlug}/appointments/${this.appointment.lead.pid}/send_email`, payload)
        .then(() => {
          this.emailSent = true
        })
        .catch(err => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response.data?.detail) {
            message = err.response.data.detail
          }
          this.showMessage(['error', message])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    clickWhatsapp () {
      let requestUrl = `https://${this.companySlug}.yezza.co/appointment/${this.appointment.lead.pid}/request`
      let appointmentUrl = `https://${this.companySlug}.yezza.co/appointment`
      let paymentMessage = ''
      if (this.appointment.booking_fee && this.lead.form.pay_now) {
        paymentMessage = 'make the payment and '
      }
      let messageApprove = `Hi ${this.appointment.lead.name},%0d%0a%0d%0aYour appointment has been approved.%0d%0a%0d%0aHere’s you appointment detail:%0d%0a${requestUrl}`
      messageApprove += `%0d%0a%0d%0aPlease make sure that you ${paymentMessage}are present on the appointment day.%0d%0a%0d%0aThank you for choosing ${this.company.name}`

      let messageReject = `Hi ${this.appointment.lead.name},%0d%0a%0d%0aYour appointment request has been declined.%0d%0a%0d%0aHere’s you appointment detail:%0d%0a${requestUrl}`
      messageReject += `%0d%0a%0d%0aWe’re very sorry for the inconvenient response. You can submit a new appointment here:%0d%0a%0d%0a${appointmentUrl}`

      let message = this.$options.filters.encodeSymbols(messageReject)
      if (this.approval) {
        message = this.$options.filters.encodeSymbols(messageApprove)
      } 
      window.open(`https://api.whatsapp.com/send?phone=${this.appointment.lead.phone}&text=${message}&app_absent=1`)
      setTimeout(() => {
        window.open(`https://api.whatsapp.com/send?phone=${this.appointment.lead.phone}&text=${message}&app_absent=1`)
      }, 200)
    },
    goToTab () {
      this.$emit('tab')
      this.closeDialog()
    },
    closeDialog () {
      this.$emit('closed')
      this.showDialog = false
    },
    closeResetDialog () {
      this.$emit('reset')
      this.showDialog = false
    }
  }
}
</script>

<style lang="scss" scoped>
.custom-box {
  border: 1px solid var(--v-neutral300);
  background-color: var(--v-neutral200);
  border-radius: 10px;
}
</style>